import { useEffect, useState } from 'react';
import { ApiError, type Api, type QualityStats } from '../api';
import { BAD_FLAGS, GOOD_FLAGS, SCORE_KEYS } from '../components/Evaluation';
import type { T } from '../i18n';

const pct = (a: number, b: number) => (b ? Math.round((a / b) * 100) : 0);
const tt = (t: T, key: string, fallback: string) => (t(key) === key ? fallback : t(key));

/**
 * ADR-047: call quality from the decision model's evaluations — scorecard, rules and mistakes, outcomes and loss
 * reasons, the questions customers asked (answered or not) and how the product kit was used. Real calls or test room.
 */
export const StatsQuality = ({ api, t, days, agentId, direction, onOpenCall }: { api: Api; t: T; days: number; agentId: string; direction: string; onOpenCall: (id: string) => void }) => {
  const [source, setSource] = useState<'real' | 'room'>('real');
  const [q, setQ] = useState<QualityStats>();
  const [error, setError] = useState<string>();
  useEffect(() => {
    setQ(undefined);
    setError(undefined);
    api
      .statsQuality({ days, source, ...(agentId ? { agentId } : {}), ...(direction ? { direction } : {}) })
      .then(setQ)
      .catch((e: unknown) => setError(e instanceof ApiError ? e.code : 'generic'));
  }, [api, days, agentId, direction, source]);

  const choices = (key: string, skip: string[] = []) => (q?.choices ?? []).filter((c) => c.key === key && !skip.includes(c.value)).sort((a, b) => b.n - a.n);
  const bars = (rows: Array<{ label: string; n: number }>, total: number) => (
    <div className="hbars">
      {rows.map((r) => (
        <div key={r.label} className="hbar">
          <span>{r.label}</span>
          <span className="hbar-track"><span className="hbar-fill" style={{ width: `${pct(r.n, total)}%` }} /></span>
          <span className="hbar-value">{r.n}</span>
        </div>
      ))}
    </div>
  );
  const questions = q?.questions ?? [];
  const unanswered = questions.filter((x) => x.answered < 0.5);
  const answeredPct = pct(questions.length - unanswered.length, questions.length);

  return (
    <div className="quality">
      <div className="row-between">
        <div>
          <h2>{t('quality.title')}</h2>
          <p className="muted small">{t('quality.subtitle')}</p>
        </div>
        <div className="segmented" role="group">
          {(['real', 'room'] as const).map((s) => <button key={s} type="button" className={source === s ? 'on' : ''} onClick={() => setSource(s)}>{t(`quality.source.${s}`)}</button>)}
        </div>
      </div>
      {error && <div className="banner error">{t(`error.${error}`) === `error.${error}` ? t('error.generic') : t(`error.${error}`)}</div>}
      {q && q.judged === 0 && <div className="empty"><p>{source === 'real' ? t('quality.emptyReal') : t('quality.empty')}</p></div>}
      {q && q.judged > 0 && (
        <>
          <div className="two-col">
            <div className="card chart-card">
              <h3>{t('quality.scorecard', { n: q.judged })}</h3>
              <div className="hbars">
                {SCORE_KEYS.map((k) => q.scores.find((s) => s.key === k)).filter((s): s is { key: string; avg: number } => !!s).map((s) => (
                  <div key={s.key} className="hbar">
                    <span>{t(`eval.score.${s.key}`)}</span>
                    <span className="hbar-track"><span className="hbar-fill" style={{ width: `${((s.avg - 1) / 4) * 100}%` }} /></span>
                    <span className="hbar-value">{s.avg.toFixed(1)} / 5</span>
                  </div>
                ))}
              </div>
            </div>
            <div className="card chart-card">
              <h3>{t('quality.rules')}</h3>
              <div className="hbars">
                {[...GOOD_FLAGS, ...BAD_FLAGS].map((k) => q.flags.find((f) => f.key === k)).filter((f): f is { key: string; yes: number; n: number } => !!f).map((f) => (
                  <div key={f.key} className="hbar">
                    <span>{t(`eval.flag.${f.key}`)}</span>
                    <span className="hbar-track"><span className="hbar-fill" style={{ width: `${pct(f.yes, f.n)}%` }} /></span>
                    <span className="hbar-value">%{pct(f.yes, f.n)}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>

          <div className="two-col">
            <div className="card chart-card">
              <h3>{t('eval.choice.outcome')}</h3>
              {bars(choices('outcome').map((c) => ({ label: tt(t, `eval.choice.outcome.${c.value}`, c.value), n: c.n })), q.judged)}
            </div>
            <div className="card chart-card">
              <h3>{t('quality.lossReasons')}</h3>
              {choices('loss_reason', ['not_applicable']).length === 0 ? <p className="muted small">{t('quality.noLoss')}</p> : bars(choices('loss_reason', ['not_applicable']).map((c) => ({ label: tt(t, `eval.choice.loss_reason.${c.value}`, c.value), n: c.n })), q.judged)}
              <h3>{t('quality.dropStage')}</h3>
              {bars(choices('drop_stage', ['none']).map((c) => ({ label: tt(t, `eval.choice.drop_stage.${c.value}`, c.value), n: c.n })), q.judged)}
            </div>
          </div>

          <div className="card">
            <div className="row-between">
              <h3>{t('quality.questions', { n: questions.length, p: answeredPct })}</h3>
            </div>
            <p className="muted small">{t('quality.questionsHint')}</p>
            {unanswered.length === 0 ? <p className="muted">{t('quality.allAnswered')}</p> : (
              <ul className="eval-questions">
                {unanswered.slice(0, 30).map((x, i) => (
                  <li key={i}>
                    <span className="tag warn">{t('eval.notAnswered')}</span>
                    {x.intent === 'objection' && <span className="tag">{t('eval.objection')}</span>}
                    <span>{x.text}</span>
                    <button type="button" className="link small" onClick={() => onOpenCall(x.callId)}>{t('quality.openCall')}</button>
                  </li>
                ))}
              </ul>
            )}
          </div>

          {q.kit.length > 0 && (
            <div className="card">
              <h3>{t('quality.kit')}</h3>
              <p className="muted small">{t('quality.kitHint')}</p>
              <div className="table-wrap">
                <table className="table">
                  <thead><tr><th>{t('quality.kitItem')}</th><th>{t('quality.kitKind')}</th><th className="num">{t('quality.kitUsed')}</th><th className="num">{t('quality.kitUnanswered')}</th></tr></thead>
                  <tbody>
                    {[...q.kit].sort((a, b) => b.used - a.used).map((k, i) => (
                      <tr key={i}>
                        <td>{k.kind === 'offering' ? <strong>{k.text}</strong> : <>{k.text} <span className="muted small">· {k.offering}</span></>}</td>
                        <td>{t(`quality.kind.${k.kind}`)}</td>
                        <td className="num">{k.used === 0 ? <span className="tag">{t('quality.neverUsed')}</span> : k.used}</td>
                        <td className="num">{k.unanswered > 0 ? <span className="tag warn">{k.unanswered}</span> : '—'}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
};
